import React, { useEffect, useMemo, useRef, useState } from "react";
import { useProperty } from "@/context/PropertyContext";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Camera, X, UploadCloud, Wallet } from "lucide-react";
import { eur, fechaCorta } from "@/lib/format";
import { toast } from "sonner";

const ESTADOS = [
  ["pendiente", "Pendiente"],
  ["en_curso", "En curso"],
  ["resuelta", "Resuelta"],
];
const PRIORIDADES = [["baja", "Baja"], ["media", "Media"], ["alta", "Alta"]];

export default function Incidents() {
  const { selectedId, selected } = useProperty();
  const [incidents, setIncidents] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [filter, setFilter] = useState("abiertas");
  const [photos, setPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [costeInput, setCosteInput] = useState("");
  const [savingCoste, setSavingCoste] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => {
    if (!selectedId) return setLoading(false);
    setLoading(true);
    Promise.all([
      api.get(`/incidents?property_id=${selectedId}`),
      api.get(`/tenants?property_id=${selectedId}`),
    ]).then(([i, t]) => {
      setIncidents(i.data || []);
      setTenants(t.data || []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [selectedId]);

  const filtered = useMemo(() => {
    if (filter === "todas") return incidents;
    return incidents.filter((i) => i.estado !== "resuelta");
  }, [incidents, filter]);

  const openNew = () => {
    setForm({ id: null, titulo: "", descripcion: "", estado: "pendiente", prioridad: "media", tenant_id: "none", habitacion: "", fecha: new Date().toISOString().slice(0, 10), presupuesto: "", proveedor: "", coste_final: null, expense_id: null });
    setPhotos([]);
    setCosteInput("");
    setOpen(true);
  };

  const openEdit = (i) => {
    setForm({ ...i, tenant_id: i.tenant_id || "none", fecha: (i.fecha || "").slice(0, 10), presupuesto: i.presupuesto ?? "", proveedor: i.proveedor || "" });
    setCosteInput(i.coste_final != null ? String(i.coste_final) : "");
    setPhotos([]);
    api.get(`/incident-photos?incident_id=${i.id}`).then((r) => setPhotos(r.data || [])).catch(() => {});
    setOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.titulo?.trim()) return toast.error("Indica un título");
    let presupuesto = null;
    if (form.presupuesto !== "" && form.presupuesto != null) {
      presupuesto = Number(form.presupuesto);
      if (Number.isNaN(presupuesto) || presupuesto < 0) return toast.error("Indica un presupuesto válido");
    }
    const payload = {
      property_id: selectedId,
      titulo: form.titulo.trim(),
      descripcion: form.descripcion || "",
      estado: form.estado,
      prioridad: form.prioridad,
      tenant_id: form.tenant_id === "none" ? null : form.tenant_id,
      habitacion: form.habitacion || "",
      fecha: form.fecha ? new Date(form.fecha).toISOString() : null,
      presupuesto,
      proveedor: form.proveedor || "",
    };
    try {
      if (form.id) await api.put(`/incidents/${form.id}`, payload);
      else await api.post("/incidents", payload);
      toast.success(form.id ? "Incidencia actualizada" : "Incidencia creada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar incidencia");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta incidencia?")) return;
    await api.delete(`/incidents/${id}`);
    load();
  };

  const handlePhotoFile = async (file) => {
    if (!file || !form?.id) return;
    if (!/^image\/(png|jpeg)$/.test(file.type)) return toast.error("Selecciona una foto PNG o JPG");
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/incidents/${form.id}/fotos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.get(`/incident-photos?incident_id=${form.id}`);
      setPhotos(r.data || []);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir la foto");
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePhoto = async (fotoId) => {
    if (!window.confirm("¿Eliminar esta foto?")) return;
    try {
      await api.delete(`/incident-photos/${fotoId}`);
      setPhotos((prev) => prev.filter((p) => p.id !== fotoId));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar la foto");
    }
  };

  const saveCoste = async () => {
    const importe = Number(costeInput);
    if (costeInput === "" || Number.isNaN(importe) || importe < 0) return toast.error("Indica un importe válido");
    setSavingCoste(true);
    try {
      const { data } = await api.post(`/incidents/${form.id}/coste`, { importe });
      setForm((f) => ({ ...f, coste_final: data.coste_final, expense_id: data.expense_id }));
      toast.success("Coste registrado como gasto");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al registrar el coste");
    } finally {
      setSavingCoste(false);
    }
  };

  const removeCoste = async () => {
    if (!window.confirm("¿Quitar el coste y eliminar el gasto asociado?")) return;
    try {
      const { data } = await api.delete(`/incidents/${form.id}/coste`);
      setForm((f) => ({ ...f, coste_final: data.coste_final, expense_id: data.expense_id }));
      setCosteInput("");
      toast.success("Coste eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar el coste");
    }
  };

  const estadoBadge = (estado) => {
    if (estado === "resuelta") return <Badge className="bg-success/15 text-success hover:bg-success/15 border-0">Resuelta</Badge>;
    if (estado === "en_curso") return <Badge className="bg-primary/15 text-primary hover:bg-primary/15 border-0">En curso</Badge>;
    return <Badge className="bg-warning/15 text-warning hover:bg-warning/15 border-0">Pendiente</Badge>;
  };

  if (!selectedId) return <div className="data-card p-10 text-center text-muted-foreground">Selecciona o crea un piso primero.</div>;

  return (
    <div data-testid="incidents-page">
      <PageHeader title="Incidencias" subtitle={selected?.nombre || "Seguimiento"}>
        <Button onClick={openNew} data-testid="add-incident-button"><Plus className="h-4 w-4 mr-1.5" /> Nueva incidencia</Button>
      </PageHeader>

      <div className="flex items-center gap-2 mb-3">
        <Label className="text-xs">Vista:</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="abiertas">Abiertas</SelectItem>
            <SelectItem value="todas">Todas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="data-card overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Incidencia</TableHead><TableHead>Inquilino/Hab.</TableHead><TableHead>Prioridad</TableHead><TableHead>Estado</TableHead><TableHead className="hidden lg:table-cell">Fecha</TableHead><TableHead className="hidden md:table-cell">Coste</TableHead><TableHead className="w-20"></TableHead></TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Cargando…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Sin incidencias</TableCell></TableRow>
            ) : filtered.map((i) => (
              <TableRow key={i.id}>
                <TableCell>
                  <p className="font-medium flex items-center gap-1.5">
                    {i.titulo}
                    {i.num_fotos > 0 && <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground"><Camera className="h-3.5 w-3.5" />{i.num_fotos}</span>}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-1">{i.descripcion || "—"}</p>
                </TableCell>
                <TableCell className="text-sm">{i.tenant_nombre || "—"}{i.habitacion ? <span className="text-muted-foreground"> · Hab. {i.habitacion}</span> : null}</TableCell>
                <TableCell className="capitalize">{i.prioridad}</TableCell>
                <TableCell>{estadoBadge(i.estado)}</TableCell>
                <TableCell className="hidden lg:table-cell font-mono text-sm">{fechaCorta(i.fecha)}</TableCell>
                <TableCell className="hidden md:table-cell font-mono text-sm">
                  {i.coste_final != null ? eur(i.coste_final) : i.presupuesto != null ? <span className="text-muted-foreground">Presup. {eur(i.presupuesto)}</span> : "—"}
                </TableCell>
                <TableCell><div className="flex justify-end gap-1"><button onClick={() => openEdit(i)} className="p-1.5 text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button><button onClick={() => remove(i.id)} className="p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button></div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">{form?.id ? "Editar incidencia" : "Nueva incidencia"}</DialogTitle>
            <DialogDescription>Registra averías, reparaciones, quejas o tareas pendientes.</DialogDescription>
          </DialogHeader>
          {form && <div className="space-y-4">
            <div><Label>Título *</Label><Input value={form.titulo} onChange={(e) => setField("titulo", e.target.value)} /></div>
            <div><Label>Descripción</Label><Input value={form.descripcion || ""} onChange={(e) => setField("descripcion", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Estado</Label><Select value={form.estado} onValueChange={(v) => setField("estado", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{ESTADOS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Prioridad</Label><Select value={form.prioridad} onValueChange={(v) => setField("prioridad", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRIORIDADES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Inquilino</Label><Select value={form.tenant_id} onValueChange={(v) => setField("tenant_id", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Sin asignar</SelectItem>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre} {t.apellidos}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Habitación</Label><Input value={form.habitacion || ""} onChange={(e) => setField("habitacion", e.target.value)} /></div>
            </div>
            <div><Label>Fecha</Label><Input type="date" value={form.fecha || ""} onChange={(e) => setField("fecha", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Proveedor</Label><Input value={form.proveedor || ""} onChange={(e) => setField("proveedor", e.target.value)} placeholder="Fontanero, electricista…" /></div>
              <div><Label>Presupuesto</Label><Input type="number" min="0" step="0.01" value={form.presupuesto ?? ""} onChange={(e) => setField("presupuesto", e.target.value)} placeholder="0,00" /></div>
            </div>

            {!form.id ? (
              <p className="text-xs text-muted-foreground rounded-md border border-dashed border-border p-3">
                Guarda la incidencia primero para poder añadir fotos y registrar el coste final.
              </p>
            ) : (
              <>
                <div className="rounded-md border border-border p-3">
                  <Label className="mb-2 block">Coste final</Label>
                  {form.expense_id ? (
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" /> Registrado como gasto en Gastos</p>
                      <button type="button" onClick={removeCoste} className="text-xs text-destructive hover:underline">Quitar</button>
                    </div>
                  ) : null}
                  <div className="flex gap-2">
                    <Input type="number" min="0" step="0.01" value={costeInput} onChange={(e) => setCosteInput(e.target.value)} placeholder="Importe final" />
                    <Button variant="outline" onClick={saveCoste} disabled={savingCoste} data-testid="save-coste-button">Guardar</Button>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-1.5"><Camera className="h-4 w-4" /> Fotos</Label>
                    <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto} data-testid="add-photo-button">
                      <UploadCloud className="h-4 w-4 mr-1.5" /> Añadir foto
                    </Button>
                    <input ref={fileInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => handlePhotoFile(e.target.files?.[0])} />
                  </div>
                  {photos.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Sin fotos todavía.</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {photos.map((p) => (
                        <div key={p.id} className="relative group aspect-square">
                          <img src={`${API}/incident-photos/${p.id}`} alt={p.filename} className="h-full w-full object-cover rounded-md border border-border" />
                          <button
                            type="button"
                            onClick={() => removePhoto(p.id)}
                            className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
